import { UserManagerSettings } from 'oidc-client-ts';
import KeyVaultConfigService from '../services/keyVaultConfig';

// Get Key Vault configuration service
const keyVaultConfig = KeyVaultConfigService.getInstance();

// Function to get OIDC configuration asynchronously from Key Vault
export const getOidcConfig = async (): Promise<UserManagerSettings> => {
  // Initialize and get configuration from Key Vault
  await keyVaultConfig.initialize();
  
  const authority = await keyVaultConfig.getAuthority();
  const clientId = await keyVaultConfig.getClientId();
  const authProvider = await keyVaultConfig.getAuthProvider();
  

  // HOTFIX: Force tenant-specific endpoint to avoid /common multi-tenant error
  const baseAuthority = 'https://login.microsoftonline.com/7cf50681-c05a-4dff-9263-d297df8c8bd9/v2.0';

  // Check if Microsoft Graph API is enabled (defaults to false to avoid admin consent requirement)
  const graphApiEnabled = process.env.REACT_APP_ENABLE_GRAPH_API === 'true';
  
  // Define scopes based on Graph API availability
  // Basic OIDC scopes (openid profile email offline_access) do NOT require admin consent
  // Graph scopes (User.Read User.ReadBasic.All) DO require admin consent
  const graphScopes = graphApiEnabled ? ' User.Read User.ReadBasic.All' : '';
  const oidcScopes = baseAuthority.includes('microsoftonline.com')
    ? `openid profile email offline_access${graphScopes}`
    : 'openid profile email';

  return {
    // OIDC Configuration for UniFirst SSO from Key Vault
    authority: baseAuthority,
    client_id: clientId || (process.env.REACT_APP_OIDC_CLIENT_ID || ''),
    
    // URLs that need to be registered with UniFirst
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: `${window.location.origin}/auth/logout-callback`,
    silent_redirect_uri: `${window.location.origin}/auth/silent-callback`,
    
    // OIDC Flow Configuration
    response_type: 'code',
    scope: oidcScopes,
    
    // Security settings
    automaticSilentRenew: true,
    includeIdTokenInSilentRenew: true,
    
    // Token lifetime settings
    accessTokenExpiringNotificationTimeInSeconds: 60, // 1 minute before expiry
    silentRequestTimeoutInSeconds: 10, // 10 seconds
    
    // Additional security
    filterProtocolClaims: true,
    loadUserInfo: true,
    
    // Metadata endpoint (Azure AD v2.0 format)
    metadataUrl: `${baseAuthority}/.well-known/openid-configuration`,
    
    // Additional settings for enterprise SSO
    extraQueryParams: {
      // Can be used for additional parameters required by UniFirst
    },
    
    // Store user session in sessionStorage for security
    userStore: undefined // Will use default sessionStorage
  };
};

// Synchronous fallback configuration (uses sync methods for immediate access)
export const getOidcConfigSync = (): UserManagerSettings => {
  // HOTFIX: Force tenant-specific endpoint to avoid /common multi-tenant error
  const baseAuthority = 'https://login.microsoftonline.com/7cf50681-c05a-4dff-9263-d297df8c8bd9/v2.0';

  // Check if Microsoft Graph API is enabled (defaults to false to avoid admin consent requirement)
  const graphApiEnabled = process.env.REACT_APP_ENABLE_GRAPH_API === 'true';
  
  // Define scopes based on Graph API availability
  const graphScopes = graphApiEnabled ? ' User.Read User.ReadBasic.All' : '';
  const oidcScopes = baseAuthority.includes('microsoftonline.com')
    ? `openid profile email offline_access${graphScopes}`
    : 'openid profile email';

  return {
    // Real configuration when authority is available
    authority: baseAuthority,
    client_id: keyVaultConfig.getClientIdSync() || (process.env.REACT_APP_OIDC_CLIENT_ID || ''),
    
    // URLs that need to be registered with UniFirst
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: `${window.location.origin}/auth/logout-callback`,
    silent_redirect_uri: `${window.location.origin}/auth/silent-callback`,
    
    // OIDC Flow Configuration
    response_type: 'code',
    scope: oidcScopes,
    
    // Security settings
    automaticSilentRenew: true,
    includeIdTokenInSilentRenew: true,
    
    // Token lifetime settings
    accessTokenExpiringNotificationTimeInSeconds: 60,
    silentRequestTimeoutInSeconds: 10,
    
    // Additional security
    filterProtocolClaims: true,
    loadUserInfo: true,
    
    // Metadata endpoint (Azure AD v2.0 format)
    metadataUrl: `${baseAuthority}/.well-known/openid-configuration`,
    
    extraQueryParams: {},
    userStore: undefined
  };
};

// Development configuration for local testing (uses working Azure AD endpoint)
export const oidcConfigDev: UserManagerSettings = {

  // HOTFIX: Force tenant-specific endpoint to avoid /common multi-tenant error
  authority: 'https://login.microsoftonline.com/7cf50681-c05a-4dff-9263-d297df8c8bd9/v2.0',
  client_id: process.env.REACT_APP_OIDC_CLIENT_ID || '',
  redirect_uri: 'http://localhost:3000/auth/callback',
  post_logout_redirect_uri: 'http://localhost:3000/auth/logout-callback',
  silent_redirect_uri: 'http://localhost:3000/auth/silent-callback',
  response_type: 'code',
  scope: 'openid profile email',
  automaticSilentRenew: false, // Disabled for dev to avoid token issues
  includeIdTokenInSilentRenew: false,
  accessTokenExpiringNotificationTimeInSeconds: 60,
  silentRequestTimeoutInSeconds: 10,
  filterProtocolClaims: true,

  loadUserInfo: true,
  metadataUrl: 'https://login.microsoftonline.com/7cf50681-c05a-4dff-9263-d297df8c8bd9/v2.0/.well-known/openid-configuration',
  extraQueryParams: {},
  userStore: undefined
};

// Default export for backward compatibility
export default getOidcConfig;